/**
 * Art Gallery — a walkable white room built with three.js.
 * Markup and data come from sections/art-gallery.liquid.
 *
 * Room units are metres. Local wall space: +z points into the room, +x is the viewer's right.
 */

const ROOM = { width: 12, depth: 10, height: 3.8 };
const EYE_HEIGHT = 1.6;
const HANG_HEIGHT = 1.5;
// Desktop mouse-look: how far (radians) the view follows the pointer, where edge turning starts, and its top speed (rad/s).
const MOUSE_LOOK = { yaw: 0.35, pitch: 0.16, edge: 0.82, turnSpeed: 1.1 };
const WALK_MARGIN = 0.7;
// Free walking stops further from the walls, so a tap near a wall never leaves the visitor nose-to-plaster.
const FLOOR_MARGIN = 1.6;
// How much of the room the polished floor mirrors (0 = matt).
const FLOOR_SHINE = 0.13;
const ENTERED_KEY = 'art-gallery-entered';
const MUTED_KEY = 'art-gallery-muted';

const PLACEHOLDER_SIZES = [
  [100, 120],
  [150, 100],
  [80, 80],
  [120, 160],
  [200, 140],
  [90, 120],
  [100, 100],
  [140, 100],
];

const root = document.querySelector('[data-art-gallery]');
if (root instanceof HTMLElement) start(root);

/** @param {HTMLElement} el */
async function start(el) {
  const config = JSON.parse(el.querySelector('[data-config]')?.textContent || '{}');

  try {
    if (!supportsWebGL()) throw new Error('WebGL unavailable');
    const THREE = await import(el.dataset.threeUrl || '');
    const gallery = new Gallery(el, config, THREE);
    await gallery.build();
  } catch (error) {
    console.error('[art-gallery]', error);
    el.classList.add('no-webgl');
  }
}

function supportsWebGL() {
  const canvas = document.createElement('canvas');
  return Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl'));
}

const easeInOut = (/** @type {number} */ t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const clamp = (/** @type {number} */ v, /** @type {number} */ min, /** @type {number} */ max) =>
  Math.min(max, Math.max(min, v));
const shortestAngle = (/** @type {number} */ a) => Math.atan2(Math.sin(a), Math.cos(a));

class Gallery {
  /**
   * @param {HTMLElement} el
   * @param {any} config
   * @param {any} THREE
   */
  constructor(el, config, THREE) {
    this.el = el;
    this.config = config;
    this.THREE = THREE;

    this.stage = /** @type {HTMLElement} */ (el.querySelector('[data-stage]'));
    this.intro = /** @type {HTMLElement} */ (el.querySelector('[data-intro]'));
    this.enterButton = /** @type {HTMLButtonElement} */ (el.querySelector('[data-enter]'));
    this.enterLabel = /** @type {HTMLElement} */ (el.querySelector('[data-enter-label]'));
    this.hud = /** @type {HTMLElement} */ (el.querySelector('[data-hud]'));
    this.counter = /** @type {HTMLElement} */ (el.querySelector('[data-counter]'));
    this.position = /** @type {HTMLElement} */ (el.querySelector('[data-position]'));
    this.panel = /** @type {HTMLElement} */ (el.querySelector('[data-panel]'));
    this.panelBody = /** @type {HTMLElement} */ (el.querySelector('[data-panel-body]'));
    this.soundButton = /** @type {HTMLButtonElement} */ (el.querySelector('[data-sound]'));
    this.closeup = /** @type {HTMLElement} */ (el.querySelector('[data-closeup]'));
    this.closeupStage = /** @type {HTMLElement} */ (el.querySelector('[data-closeup-stage]'));
    this.closeupHint = /** @type {HTMLElement} */ (el.querySelector('[data-closeup-hint]'));
    this.closeupHintText = this.closeupHint.textContent || '';
    // The mascot's speech bubble; absent when he has nothing to say.
    this.bubble = /** @type {HTMLElement | null} */ (el.querySelector('[data-bubble]'));
    this.bubblePending = false;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.desktop = window.matchMedia('(min-width: 750px)');
    this.touch = window.matchMedia('(pointer: coarse)').matches;
    this.fontFamily = getComputedStyle(el).fontFamily || 'sans-serif';

    this.paintings = this.preparePaintings();
    /** @type {any[]} */
    this.stops = [];
    /** @type {any[]} */
    this.pickables = [];
    this.current = -1;
    this.entered = false;
    this.arrived = false;
    this.needsRender = true;
    this.lastFrame = 0;

    this.yaw = 0;
    this.pitch = 0;
    this.lookYaw = 0;
    this.lookPitch = 0;
    // Mouse-look: the pointer's place on screen (-1..1 from the centre) steers the view around an anchor direction.
    this.mouse = { active: false, nx: 0, ny: 0 };
    this.anchorYaw = 0;
    this.anchorPitch = 0;
    /** @type {any} */
    this.tween = null;
    this.offset = { x: 0, y: 0 };
    this.offsetTarget = { x: 0, y: 0 };
    /**
     * The mascot in the middle of the room: his whole footprint, where his body stands within it, and the top of his head.
     * @type {{ halfX: number, halfZ: number, height: number, body: { x: number, z: number, radius: number }, head: any } | null}
     */
    this.mascot = null;
    /** @type {any} */
    this.sound = null;
    this.soundOn = false;
    this.resumeSound = false;
  }

  /** Normalises config paintings; falls back to placeholder canvases when none are configured. */
  preparePaintings() {
    /** @type {any[]} */
    let list = this.config.paintings || [];

    if (!list.length) {
      list = PLACEHOLDER_SIZES.map(([width, height], index) => ({
        title: `Painting ${index + 1}`,
        width,
        height,
        available: true,
        placeholder: true,
        garments: [],
      }));
    }

    return list.map((painting) => {
      const { story, details } = parseDescription(painting.descriptionHtml, painting.title);
      // "Size: 55 × 65 cm" in the description stands in for missing size metafields.
      const sizeDetail = details.find((detail) => /^(size|dimensions?)$/i.test(detail.label));
      const sizeMatch = sizeDetail?.value.match(/(\d+(?:[.,]\d+)?)\s*[×x]\s*(\d+(?:[.,]\d+)?)/i);
      if (sizeMatch && !Number(painting.width) && !Number(painting.height)) {
        painting = { ...painting, width: parseFloat(sizeMatch[1].replace(',', '.')), height: parseFloat(sizeMatch[2].replace(',', '.')) };
      }

      const aspect = painting.aspect || (painting.width && painting.height ? painting.width / painting.height : 0.8);
      let w = Number(painting.width) / 100 || 0;
      let h = Number(painting.height) / 100 || 0;
      const sizeKnown = Boolean(w || h);

      if (!w && !h) {
        // No real size on the product: hang it with a 1 m long side.
        w = aspect >= 1 ? 1 : aspect;
        h = aspect >= 1 ? 1 / aspect : 1;
      } else if (!w) {
        w = h * aspect;
      } else if (!h) {
        h = w / aspect;
      }

      return { ...painting, w, h, sizeKnown, story, details, sizeLabel: sizeDetail?.value || '' };
    });
  }

  async build() {
    const { THREE } = this;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setClearColor(0xf3f2ef);
    this.stage.appendChild(this.renderer.domElement);
    // Phones drop the WebGL context when the tab sits in the background; redraw once it is back.
    this.renderer.domElement.addEventListener('webglcontextrestored', () => (this.needsRender = true));
    this.maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 60);
    this.camera.rotation.order = 'YXZ';
    this.camera.position.set(0, EYE_HEIGHT, ROOM.depth / 2 - 0.9);
    this.raycaster = new THREE.Raycaster();

    try {
      await Promise.race([document.fonts.ready, new Promise((resolve) => setTimeout(resolve, 1500))]);
    } catch (_) {
      /* canvas text falls back to the default font */
    }

    this.buildRoom();
    const hung = this.hangPaintings();
    this.buildArtistWall();
    this.buildFloorMarker();
    this.buildReflection();
    const loaded = Promise.all([hung, this.seatMascot()]);

    this.resize();
    new ResizeObserver(() => this.resize()).observe(this.el);
    this.bindEvents();
    this.renderer.setAnimationLoop(() => this.frame());

    // Never keep visitors waiting on a slow image: the room opens after 8s at the latest.
    await Promise.race([loaded, new Promise((resolve) => setTimeout(resolve, 8000))]);
    this.ready();
  }

  /* ---------- Room ---------- */

  /**
   * @param {number} width
   * @param {number} height
   * @param {(ctx: CanvasRenderingContext2D, width: number, height: number) => void} draw
   */
  canvasTexture(width, height, draw) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
    draw(ctx, width, height);
    const texture = new this.THREE.CanvasTexture(canvas);
    texture.colorSpace = this.THREE.SRGBColorSpace;
    texture.anisotropy = this.maxAnisotropy;
    return texture;
  }

  buildRoom() {
    const { THREE, scene } = this;
    const { width: W, depth: D, height: H } = ROOM;

    // The room is unlit: surfaces carry baked gradients so whites stay white and it runs well on phones.
    const wallTexture = this.canvasTexture(256, 256, (ctx, w, h) => {
      const gradient = ctx.createLinearGradient(0, 0, 0, h);
      gradient.addColorStop(0, '#e9e8e4');
      gradient.addColorStop(0.3, '#f1f0ed');
      gradient.addColorStop(0.8, '#eeedea');
      gradient.addColorStop(1, '#dedcd7');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);
      // Light falls off in the corners, where two walls meet.
      shadeEdges(ctx, w, h, w * 0.1, 0, 0.13);
    });

    /** @type {[number, number, number, number, number][]} x, z, rotation, length, tint */
    const walls = [
      [0, -D / 2, 0, W, 0xffffff],
      [0, D / 2, Math.PI, W, 0xf6f6f6],
      [-W / 2, 0, Math.PI / 2, D, 0xf1f1f1],
      [W / 2, 0, -Math.PI / 2, D, 0xf1f1f1],
    ];

    for (const [x, z, rotation, length, tint] of walls) {
      const wall = new THREE.Mesh(
        new THREE.PlaneGeometry(length, H),
        new THREE.MeshBasicMaterial({ map: wallTexture, color: tint })
      );
      wall.position.set(x, H / 2, z);
      wall.rotation.y = rotation;
      scene.add(wall);

      const skirting = new THREE.Mesh(
        new THREE.PlaneGeometry(length, 0.05),
        new THREE.MeshBasicMaterial({ color: 0xc9c6bf })
      );
      skirting.position.set(x, 0.025, z);
      skirting.rotation.y = rotation;
      skirting.translateZ(0.003);
      scene.add(skirting);
    }

    // Polished concrete: cloudy greys, saw-cut joints, daylight pooling in the middle, shade along the walls.
    const floorTexture = this.canvasTexture(1024, 1024, (ctx, w, h) => {
      ctx.fillStyle = '#bfbcb5';
      ctx.fillRect(0, 0, w, h);

      const random = seededRandom(7);
      for (let i = 0; i < 280; i++) {
        const x = random() * w;
        const y = random() * h;
        const radius = 30 + random() * 160;
        const tone = random() < 0.5 ? '255, 255, 255' : '72, 66, 56';
        const cloud = ctx.createRadialGradient(x, y, 0, x, y, radius);
        cloud.addColorStop(0, `rgba(${tone}, ${0.03 + random() * 0.05})`);
        cloud.addColorStop(1, `rgba(${tone}, 0)`);
        ctx.fillStyle = cloud;
        ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
      }

      const pool = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w * 0.55);
      pool.addColorStop(0, 'rgba(255, 255, 255, 0.16)');
      pool.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = pool;
      ctx.fillRect(0, 0, w, h);

      shadeEdges(ctx, w, h, w * 0.07, h * 0.08, 0.32);

      // Grit. Close to the visitor's feet every texture pixel is stretched large, and without this
      // the rounding steps of the soft gradients above show up as a faint grid.
      const pixels = ctx.getImageData(0, 0, w, h);
      for (let i = 0; i < pixels.data.length; i += 4) {
        const grit = (random() - 0.5) * 9;
        pixels.data[i] += grit;
        pixels.data[i + 1] += grit;
        pixels.data[i + 2] += grit;
      }
      ctx.putImageData(pixels, 0, 0);

      ctx.fillStyle = 'rgba(60, 54, 46, 0.2)';
      for (const third of [1 / 3, 2 / 3]) {
        ctx.fillRect(Math.round(w * third), 0, 1, h);
        ctx.fillRect(0, Math.round(h * third), w, 1);
      }
    });

    // Slightly see-through, so the mirrored room underneath (buildReflection) shows as a sheen.
    this.floor = new THREE.Mesh(
      new THREE.PlaneGeometry(W, D),
      new THREE.MeshBasicMaterial({ map: floorTexture, transparent: true, opacity: 1 - FLOOR_SHINE })
    );
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.userData.floor = true;
    this.floor.userData.grounded = true;
    scene.add(this.floor);
    this.pickables.push(this.floor);

    const ceilingTexture = this.canvasTexture(256, 256, (ctx, w, h) => {
      ctx.fillStyle = '#e6e5e1';
      ctx.fillRect(0, 0, w, h);
      shadeEdges(ctx, w, h, w * 0.1, h * 0.12, 0.12);
    });
    const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(W, D), new THREE.MeshBasicMaterial({ map: ceilingTexture }));
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.y = H;
    scene.add(ceiling);

    const skylightTexture = this.canvasTexture(256, 256, (ctx, w, h) => {
      ctx.fillStyle = '#e6e5e1';
      ctx.fillRect(0, 0, w, h);
      ctx.shadowColor = '#ffffff';
      ctx.shadowBlur = 26;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(34, 34, w - 68, h - 68);
    });
    const skylight = new THREE.Mesh(
      new THREE.PlaneGeometry(W * 0.56, D * 0.5),
      new THREE.MeshBasicMaterial({ map: skylightTexture })
    );
    skylight.rotation.x = Math.PI / 2;
    skylight.position.y = H - 0.005;
    scene.add(skylight);

    // Lighting track: a rectangle of rail 1.7 m in from the walls.
    const railMaterial = new THREE.MeshBasicMaterial({ color: 0x2b2b2b });
    const inset = 1.7;
    for (const side of [-1, 1]) {
      const alongX = new THREE.Mesh(new THREE.BoxGeometry(W - inset * 2, 0.035, 0.035), railMaterial);
      alongX.position.set(0, H - 0.05, side * (D / 2 - inset));
      const alongZ = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.035, D - inset * 2), railMaterial);
      alongZ.position.set(side * (W / 2 - inset), H - 0.05, 0);
      scene.add(alongX, alongZ);
    }
    this.railMaterial = railMaterial;
    this.railInset = inset;
  }

  /** Clockwise tour from the entrance: left wall, back wall, right wall, then the front wall beside the artist text. */
  wallRuns() {
    const { THREE } = this;
    const hw = ROOM.width / 2;
    const hd = ROOM.depth / 2;

    return [
      { start: new THREE.Vector3(-hw, 0, hd), dir: new THREE.Vector3(0, 0, -1), normal: new THREE.Vector3(1, 0, 0), length: ROOM.depth },
      { start: new THREE.Vector3(-hw, 0, -hd), dir: new THREE.Vector3(1, 0, 0), normal: new THREE.Vector3(0, 0, 1), length: ROOM.width },
      { start: new THREE.Vector3(hw, 0, -hd), dir: new THREE.Vector3(0, 0, 1), normal: new THREE.Vector3(-1, 0, 0), length: ROOM.depth },
      { start: new THREE.Vector3(hw, 0, hd), dir: new THREE.Vector3(-1, 0, 0), normal: new THREE.Vector3(0, 0, -1), length: ROOM.width / 2 },
    ];
  }

  /** Splits the paintings over the walls in proportion to wall length. */
  hangPaintings() {
    const { THREE, scene } = this;
    const runs = this.wallRuns();
    const total = runs.reduce((sum, run) => sum + run.length, 0);
    const count = this.paintings.length;

    const shares = runs.map((run) => (run.length / total) * count);
    const counts = shares.map(Math.floor);
    let remaining = count - counts.reduce((sum, value) => sum + value, 0);
    const byRemainder = shares.map((share, index) => ({ index, rest: share - Math.floor(share) })).sort((a, b) => b.rest - a.rest);
    for (let i = 0; remaining > 0; i++, remaining--) counts[byRemainder[i % runs.length].index]++;

    const shadowTexture = this.canvasTexture(256, 256, (ctx, w, h) => {
      ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
      ctx.shadowBlur = 28;
      ctx.shadowOffsetY = 6;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(44, 40, w - 88, h - 88);
      // Keep only the blur's alpha; any colour noise Safari leaves in the soft edge would show up as speckles.
      const pixels = ctx.getImageData(0, 0, w, h);
      for (let i = 0; i < pixels.data.length; i += 4) pixels.data[i] = pixels.data[i + 1] = pixels.data[i + 2] = 0;
      ctx.putImageData(pixels, 0, 0);
    });
    // Written pixel by pixel in pure white: Safari dithers canvas gradients, and in a nearly transparent texture that
    // noise turns into coloured speckles on the wall.
    const washTexture = this.canvasTexture(256, 256, (ctx, w, h) => {
      const pixels = ctx.createImageData(w, h);
      const radius = w / 2;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const t = Math.hypot(x + 0.5 - w / 2, y + 0.5 - h * 0.42) / radius;
          const alpha = t < 0.55 ? 0.85 - (0.5 * t) / 0.55 : t < 1 ? 0.35 * (1 - (t - 0.55) / 0.45) : 0;
          const i = (y * w + x) * 4;
          pixels.data[i] = pixels.data[i + 1] = pixels.data[i + 2] = 255;
          pixels.data[i + 3] = Math.round(alpha * 255);
        }
      }
      ctx.putImageData(pixels, 0, 0);
    });

    const edgeMaterial = new THREE.MeshBasicMaterial({ color: 0xe6e1d6 });
    const spotGeometry = new THREE.CylinderGeometry(0.04, 0.055, 0.17, 20);
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    const lowMemory = /** @type {any} */ (navigator).deviceMemory && /** @type {any} */ (navigator).deviceMemory <= 2;
    // Every canvas costs texture memory: weak devices get small files, and phones medium ones once the room is full.
    const imageKey = lowMemory ? 'imageSmall' : this.touch && count > 8 ? 'imageMedium' : 'image';

    /** @type {Promise<void>[]} */
    const loads = [];
    let done = 0;
    let index = 0;

    runs.forEach((run, runIndex) => {
      for (let slot = 0; slot < counts[runIndex]; slot++, index++) {
        const painting = this.paintings[index];
        const stopIndex = index;
        const along = (run.length * (slot + 0.5)) / counts[runIndex];
        const center = run.start.clone().addScaledVector(run.dir, along);
        center.y = HANG_HEIGHT;

        const group = new THREE.Group();
        group.position.copy(center);
        group.rotation.y = Math.atan2(run.normal.x, run.normal.z);
        scene.add(group);

        const wash = new THREE.Mesh(
          new THREE.PlaneGeometry(painting.w + 2, painting.h + 2.2),
          new THREE.MeshBasicMaterial({ map: washTexture, transparent: true, depthWrite: false })
        );
        wash.position.set(0, 0.25, 0.004);
        wash.renderOrder = 1;

        const shadow = new THREE.Mesh(
          new THREE.PlaneGeometry(painting.w * 1.34, painting.h * 1.34),
          new THREE.MeshBasicMaterial({ map: shadowTexture, transparent: true, depthWrite: false, opacity: 0.55 })
        );
        shadow.position.set(0, -0.035, 0.008);
        shadow.renderOrder = 2;

        const faceMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
        const canvasMesh = new THREE.Mesh(new THREE.BoxGeometry(painting.w, painting.h, 0.04), [
          edgeMaterial,
          edgeMaterial,
          edgeMaterial,
          edgeMaterial,
          faceMaterial,
          edgeMaterial,
        ]);
        canvasMesh.position.z = 0.032;
        canvasMesh.userData.stop = index;

        const label = new THREE.Mesh(
          new THREE.PlaneGeometry(0.3, 0.27),
          new THREE.MeshBasicMaterial({ map: this.labelTexture(painting) })
        );
        label.position.set(painting.w / 2 + 0.32, 1.28 - HANG_HEIGHT, 0.006);
        label.userData.stop = index;

        group.add(wash, shadow, canvasMesh, label);
        this.pickables.push(canvasMesh, label);

        const spot = new THREE.Mesh(spotGeometry, this.railMaterial);
        spot.position.copy(center).addScaledVector(run.normal, this.railInset);
        spot.position.y = ROOM.height - 0.15;
        spot.lookAt(center.x, HANG_HEIGHT + 0.6, center.z);
        spot.rotateX(Math.PI / 2);
        scene.add(spot);

        if (painting.placeholder || !painting.image) {
          faceMaterial.map = this.placeholderTexture(index, painting.w / painting.h);
        } else {
          const url = painting[imageKey] || painting.image;
          loads.push(
            new Promise((resolve) => {
              loader.load(
                url,
                (/** @type {any} */ loaded) => {
                  // Cut-out photos (transparent PNG) would show their empty margins as black: trim to the work itself.
                  const trimmed = trimTransparent(loaded.image, '#e6e1d6');
                  const texture = trimmed ? new THREE.CanvasTexture(trimmed) : loaded;
                  if (trimmed) {
                    loaded.dispose();
                    const width = painting.h * (trimmed.width / trimmed.height);
                    const stretch = width / painting.w;
                    canvasMesh.scale.x = stretch;
                    shadow.scale.x = stretch;
                    wash.scale.x = (width + 2) / (painting.w + 2);
                    label.position.x = width / 2 + 0.32;
                    painting.w = width;
                    this.stops[stopIndex].w = width;
                  }
                  texture.colorSpace = THREE.SRGBColorSpace;
                  texture.anisotropy = this.maxAnisotropy;
                  faceMaterial.map = texture;
                  faceMaterial.needsUpdate = true;
                  this.needsRender = true;
                  done++;
                  if (!this.isReady) this.enterLabel.textContent = `Hanging the paintings… ${done}/${count}`;
                  resolve();
                },
                undefined,
                () => {
                  faceMaterial.map = this.placeholderTexture(stopIndex, painting.w / painting.h);
                  faceMaterial.needsUpdate = true;
                  resolve();
                }
              );
            })
          );
        }

        this.stops.push({ painting, center, normal: run.normal.clone(), w: painting.w, h: painting.h });
      }
    });

    return Promise.all(loads);
  }

  /** @param {any} painting */
  labelTexture(painting) {
    const family = this.fontFamily;
    // The intro's small line ("Steven Blanco"): the artist wall's heading may be a show title instead of a name.
    const artist = this.config.artist?.byline || this.config.artist?.name || '';

    // Museum-label medium line: the shortest of the materials/technique details, which read like "Acrylic paint and sand".
    const medium = (painting.details || [])
      .filter((/** @type {any} */ detail) => /^(materials?|paint|medium|technique)$/i.test(detail.label))
      .map((/** @type {any} */ detail) => detail.value)
      .sort((/** @type {string} */ a, /** @type {string} */ b) => a.length - b.length)[0];

    return this.canvasTexture(600, 540, (ctx, w, h) => {
      ctx.fillStyle = '#fbfbf9';
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = '#111';
      ctx.textBaseline = 'top';

      ctx.font = `500 26px ${family}`;
      ctx.fillStyle = '#6b6b6b';
      ctx.fillText(artist.toUpperCase(), 40, 40);

      ctx.fillStyle = '#111';
      ctx.font = `600 44px ${family}`;
      let y = 86;
      for (const line of wrapText(ctx, painting.title || '', w - 80).slice(0, 2)) {
        ctx.fillText(line, 40, y);
        y += 54;
      }

      ctx.font = `400 28px ${family}`;
      ctx.fillStyle = '#444';
      y += 14;
      if (painting.sizeKnown) {
        ctx.fillText(sizeText(painting), 40, y);
        y += 44;
      }

      if (medium) {
        ctx.font = `400 25px ${family}`;
        ctx.fillStyle = '#6b6b6b';
        const lines = wrapText(ctx, medium, w - 80);
        const shown = lines.slice(0, 3);
        if (lines.length > shown.length) shown[shown.length - 1] = `${shown[shown.length - 1].replace(/[\s,.;:]+$/, '')}…`;
        for (const line of shown) {
          ctx.fillText(line, 40, y);
          y += 34;
        }
        ctx.font = `400 28px ${family}`;
        ctx.fillStyle = '#444';
      }

      const bottom = h - 70;
      if (painting.available) {
        if (painting.price) ctx.fillText(painting.price, 40, bottom);
      } else {
        ctx.fillStyle = '#c4281c';
        ctx.beginPath();
        ctx.arc(52, bottom + 16, 12, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillText('Sold', 78, bottom);
      }
    });
  }

  /**
   * Abstract colour field so the room can be judged before the real photographs arrive.
   * @param {number} index
   * @param {number} aspect
   */
  placeholderTexture(index, aspect) {
    const palettes = [
      ['#d9532b', '#f2c14e', '#1d3557'],
      ['#264653', '#2a9d8f', '#e9c46a'],
      ['#5f0f40', '#fb8b24', '#0f4c5c'],
      ['#1b1b1e', '#d8572a', '#f7b538'],
      ['#3d348b', '#f18701', '#7678ed'],
      ['#606c38', '#dda15e', '#283618'],
      ['#003049', '#d62828', '#fcbf49'],
      ['#22223b', '#c9ada7', '#9a8c98'],
    ];
    const palette = palettes[index % palettes.length];
    const height = 640;
    const width = Math.round(height * aspect);
    const random = seededRandom(index * 97 + 13);

    return this.canvasTexture(width, height, (ctx, w, h) => {
      ctx.fillStyle = palette[2];
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < 26; i++) {
        ctx.globalAlpha = 0.35 + random() * 0.5;
        ctx.strokeStyle = palette[i % 3];
        ctx.lineWidth = 12 + random() * 70;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(random() * w, random() * h);
        ctx.bezierCurveTo(random() * w, random() * h, random() * w, random() * h, random() * w, random() * h);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    });
  }

  buildArtistWall() {
    const { THREE, scene } = this;
    const artist = this.config.artist || {};
    if (!artist.name && !artist.text) return;

    const normal = new THREE.Vector3(0, 0, -1);
    const group = new THREE.Group();
    group.position.set(-ROOM.width / 4, HANG_HEIGHT, ROOM.depth / 2);
    group.rotation.y = Math.PI;
    scene.add(group);

    const stopIndex = this.stops.length;
    const hasPortrait = Boolean(artist.image);
    const family = this.fontFamily;

    // Set the type first, so the text block is exactly as large as its words and the camera can frame it.
    const scale = 580; // canvas pixels per metre of wall
    const column = (hasPortrait ? 2.7 : 3.6) * scale;
    const maxHeight = 2.4 * scale;
    const measure = /** @type {CanvasRenderingContext2D} */ (document.createElement('canvas').getContext('2d'));
    /** @type {{ line: string, font: string, color: string, y: number }[]} */
    const lines = [];
    let widest = 0;
    let y = 20;

    /** @param {string} text @param {string} font @param {string} color @param {number} leading */
    const set = (text, font, color, leading) => {
      measure.font = font;
      for (const line of wrapText(measure, text, column)) {
        if (y + leading > maxHeight) return;
        lines.push({ line, font, color, y });
        widest = Math.max(widest, measure.measureText(line).width);
        y += leading;
      }
    };

    set(artist.name || '', `600 130px ${family}`, '#111', 132);
    if (artist.subtitle) {
      y += 6;
      set(artist.subtitle, `500 56px ${family}`, '#6b6b6b', 72);
    }
    y += 40;
    for (const paragraph of String(artist.text || '').split(/\n+/)) {
      set(paragraph, `400 40px ${family}`, '#333', 58);
      y += 26;
    }

    const textTexture = this.canvasTexture(Math.ceil(widest) + 8, y, (ctx) => {
      ctx.textBaseline = 'top';
      for (const { line, font, color, y: top } of lines) {
        ctx.font = font;
        ctx.fillStyle = color;
        ctx.fillText(line, 0, top);
      }
    });
    const textWidth = textTexture.image.width / scale;
    const textHeight = textTexture.image.height / scale;

    // Portrait and text sit side by side with their tops aligned, the pair centred on eye level.
    const portraitWidth = 1.15;
    const portraitHeight = hasPortrait ? Math.min(1.7, portraitWidth / (artist.imageAspect || 0.8)) : 0;
    const gap = hasPortrait ? 0.3 : 0;
    const width = (hasPortrait ? portraitWidth : 0) + gap + textWidth;
    const top = 0.15 + Math.max(portraitHeight, textHeight) / 2;
    const textLeft = -width / 2 + (hasPortrait ? portraitWidth + gap : 0);
    let bottom = top - Math.max(portraitHeight, textHeight);

    const text = new THREE.Mesh(
      new THREE.PlaneGeometry(textWidth, textHeight),
      new THREE.MeshBasicMaterial({ map: textTexture, transparent: true, depthWrite: false })
    );
    text.position.set(textLeft + textWidth / 2, top - textHeight / 2, 0.006);
    text.userData.stop = stopIndex;
    group.add(text);
    this.pickables.push(text);

    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    /** @param {string} url @param {any} material */
    const apply = (url, material) =>
      loader.load(url, (/** @type {any} */ texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.anisotropy = this.maxAnisotropy;
        material.map = texture;
        material.needsUpdate = true;
        this.needsRender = true;
      });

    if (hasPortrait) {
      const material = new THREE.MeshBasicMaterial({ color: 0xffffff });
      const portrait = new THREE.Mesh(new THREE.PlaneGeometry(portraitWidth, portraitHeight), material);
      portrait.position.set(-width / 2 + portraitWidth / 2, top - portraitHeight / 2, 0.008);
      portrait.userData.stop = stopIndex;
      group.add(portrait);
      this.pickables.push(portrait);
      apply(artist.image, material);
    }

    if (artist.signature) {
      const signatureWidth = 0.9;
      const signatureHeight = signatureWidth / (artist.signatureAspect || 2.5);
      const material = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false });
      const signature = new THREE.Mesh(new THREE.PlaneGeometry(signatureWidth, signatureHeight), material);
      signature.position.set(textLeft + signatureWidth / 2, top - textHeight - 0.12 - signatureHeight / 2, 0.007);
      group.add(signature);
      apply(artist.signature, material);
      bottom = Math.min(bottom, top - textHeight - 0.12 - signatureHeight);
    }

    group.updateMatrixWorld();
    const center = group.localToWorld(new THREE.Vector3(0, (top + bottom) / 2, 0));
    this.stops.push({ artist: true, center, normal, w: width, h: top - bottom });
  }

  /**
   * The mascot: a 3D scan (GLB file) seated in the middle of the room, facing the entrance.
   * Resolves once he is in place, or at once when no model is set. A model that fails to load leaves the room empty.
   */
  async seatMascot() {
    const { THREE, scene } = this;
    const settings = this.config.mascot;
    const url = settings && ((this.touch && settings.modelSmall) || settings.model);
    if (!url) return;

    try {
      const { GLTFLoader } = await import(this.el.dataset.gltfUrl || '');
      const model = (await new GLTFLoader().loadAsync(url)).scene;

      // A scan carries its light in its photographs, so it is shown as shot, like the rest of this unlit room.
      model.traverse((/** @type {any} */ child) => {
        if (!child.isMesh) return;
        const scanned = child.material;
        for (const unused of ['normalMap', 'aoMap', 'roughnessMap', 'metalnessMap', 'emissiveMap']) {
          discardTexture(scanned[unused]);
        }
        child.material = new THREE.MeshBasicMaterial({
          map: this.capTexture(scanned.map),
          color: scanned.map ? 0xffffff : scanned.color,
          side: scanned.side,
        });
        scanned.dispose();
      });

      // Scale him to his real height and stand him on the floor, centred on his own footprint.
      const box = new THREE.Box3().setFromObject(model);
      const scale = (settings.height || 222) / 100 / (box.max.y - box.min.y);
      const middle = box.getCenter(new THREE.Vector3());
      model.scale.setScalar(scale);
      model.position.set(-middle.x * scale, -box.min.y * scale + 0.003, -middle.z * scale);

      const figure = new THREE.Group();
      figure.rotation.y = ((settings.turn || 0) * Math.PI) / 180;
      figure.add(model);
      scene.add(figure);
      this.reflect(figure);

      figure.updateMatrixWorld(true);
      const size = new THREE.Box3().setFromObject(figure).getSize(new THREE.Vector3());

      // His body: everything that stands taller than the things lying around him on the floor.
      // His head: the top of that, where the speech bubble points.
      const body = new THREE.Box3();
      const head = new THREE.Box3();
      const point = new THREE.Vector3();
      model.traverse((/** @type {any} */ child) => {
        if (!child.isMesh) return;
        const positions = child.geometry.attributes.position;
        for (let i = 0; i < positions.count; i++) {
          point.fromBufferAttribute(positions, i).applyMatrix4(child.matrixWorld);
          if (point.y > size.y * 0.3) body.expandByPoint(point);
          if (point.y > size.y * 0.85) head.expandByPoint(point);
        }
      });
      const bodySize = body.getSize(new THREE.Vector3());
      const bodyMiddle = body.getCenter(new THREE.Vector3());
      const headMiddle = head.getCenter(new THREE.Vector3());

      this.mascot = {
        halfX: size.x / 2,
        halfZ: size.z / 2,
        height: size.y,
        body: { x: bodyMiddle.x, z: bodyMiddle.z, radius: Math.hypot(bodySize.x, bodySize.z) / 2 },
        head: new THREE.Vector3(headMiddle.x, size.y, headMiddle.z),
      };

      // Taps are tested against two plain boxes, his body and the ground he sits on, because testing
      // every triangle of the scan would be slow. One box around everything would also catch taps
      // meant for the paintings behind him.
      /** @type {[number, number, number, number, number, number][]} width, height, depth, x, y, z */
      const targets = [
        [bodySize.x, body.max.y, bodySize.z, bodyMiddle.x, body.max.y / 2, bodyMiddle.z],
        [size.x, size.y * 0.12, size.z, 0, size.y * 0.06, 0],
      ];
      for (const [width, height, depth, x, y, z] of targets) {
        const target = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth));
        target.position.set(x, y, z);
        target.visible = false;
        target.userData.mascot = true;
        scene.add(target);
        this.pickables.push(target);
      }

      this.needsRender = true;
    } catch (error) {
      console.error('[art-gallery] mascot', error);
    }
  }

  /**
   * Scans come with textures far larger than a screen can show; shrink them to what this device holds comfortably.
   * @param {any} texture
   */
  capTexture(texture) {
    if (!texture) return null;
    const image = texture.image;
    const limit = Math.min(this.renderer.capabilities.maxTextureSize, this.touch ? 2048 : 4096);
    const scale = limit / Math.max(image.width, image.height);

    if (scale >= 1) {
      texture.anisotropy = this.maxAnisotropy;
      return texture;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);
    /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d')).drawImage(image, 0, 0, canvas.width, canvas.height);

    const small = new this.THREE.CanvasTexture(canvas);
    small.flipY = texture.flipY;
    small.colorSpace = texture.colorSpace;
    small.wrapS = texture.wrapS;
    small.wrapT = texture.wrapT;
    small.anisotropy = this.maxAnisotropy;
    discardTexture(texture);
    return small;
  }

  buildFloorMarker() {
    const { THREE } = this;
    this.marker = new THREE.Mesh(
      new THREE.RingGeometry(0.17, 0.2, 48),
      new THREE.MeshBasicMaterial({ color: 0x111111, transparent: true, opacity: 0.4, depthWrite: false })
    );
    this.marker.rotation.x = -Math.PI / 2;
    this.marker.position.y = 0.008;
    this.marker.renderOrder = 3;
    this.marker.userData.grounded = true;
    this.marker.visible = false;
    this.scene.add(this.marker);
  }

  /**
   * The floor's sheen: a copy of the room hung upside down under the see-through floor.
   * Copies share materials with the originals, so paintings appear in the reflection as they load.
   */
  buildReflection() {
    this.mirror = new this.THREE.Group();
    this.mirror.scale.y = -1;

    for (const object of this.scene.children) {
      if (!object.userData.grounded) this.reflect(object);
    }

    this.scene.add(this.mirror);
  }

  /**
   * Adds an upside-down copy of something in the room to the floor's reflection.
   * @param {any} object
   */
  reflect(object) {
    const copy = object.clone();
    // Draw before the floor, so the floor blends over the copies and not the other way round.
    copy.traverse((/** @type {any} */ child) => (child.renderOrder -= 10));
    this.mirror.add(copy);
  }

  /* ---------- Lifecycle ---------- */

  ready() {
    this.isReady = true;
    this.enterButton.disabled = false;
    this.enterLabel.textContent = this.enterLabel.dataset.readyLabel || 'Enter the gallery';
    this.needsRender = true;

    // Coming back from a product page: step straight back in front of the painting.
    let returning = false;
    try {
      returning = sessionStorage.getItem(ENTERED_KEY) === '1';
    } catch (_) {
      /* storage blocked */
    }
    if (returning && new URLSearchParams(location.search).has('painting')) this.enter();
  }

  enter() {
    if (this.entered || !this.isReady) return;
    this.entered = true;
    this.el.classList.add('is-immersive');
    document.documentElement.classList.add('art-gallery-open');
    this.intro.classList.add('is-leaving');
    this.hud.hidden = false;
    this.resize();

    try {
      sessionStorage.setItem(ENTERED_KEY, '1');
    } catch (_) {
      /* storage blocked */
    }

    // Music starts with the visit; the Enter click is the gesture browsers require for audio.
    if (!this.muted()) this.setSound(true);

    const handle = new URLSearchParams(location.search).get('painting');
    const linked = handle ? this.stops.findIndex((stop) => stop.painting?.handle === handle) : -1;
    if (linked >= 0) {
      this.focus(linked);
    } else {
      // First arrival: a few slow steps in from the door, stopping well short of the mascot.
      if (!this.arrived) {
        const stop = Math.max(ROOM.depth / 2 - 2.8, this.mascot ? this.mascot.halfZ + 1.4 : 0);
        const inside = new this.THREE.Vector3(0, EYE_HEIGHT, stop);
        this.glide({ position: inside, yaw: 0, pitch: 0 }, { duration: 2600, loose: true });
      }
      this.updateCounter();
    }
    this.arrived = true;
  }

  exit() {
    this.entered = false;
    this.hidePanel();
    this.hideBubble();
    this.setSound(false);
    this.el.classList.remove('is-immersive');
    document.documentElement.classList.remove('art-gallery-open');
    this.intro.classList.remove('is-leaving');
    this.hud.hidden = true;
    this.setUrl(null);
    try {
      sessionStorage.removeItem(ENTERED_KEY);
    } catch (_) {
      /* storage blocked */
    }
    this.resize();
  }

  resize() {
    const width = this.el.clientWidth;
    const height = this.el.clientHeight;
    if (!width || !height) return;

    this.width = width;
    this.height = height;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.fov = width < height ? 72 : 58;
    this.camera.updateProjectionMatrix();

    if (this.entered && this.current >= 0 && !this.tween) this.refit();
    this.needsRender = true;
  }

  frame() {
    const now = performance.now();
    // Easing is tied to the clock, so looking around feels the same on 60 Hz and 120 Hz screens.
    const elapsed = Math.min(now - this.lastFrame, 100);
    this.lastFrame = now;

    if (this.tween) {
      const { from, to, via, start, duration } = this.tween;
      const t = clamp((now - start) / duration, 0, 1);
      const k = easeInOut(t);
      if (via) {
        // Curved walk around the mascot: from, bending towards via, to.
        const a = (1 - k) * (1 - k);
        const b = 2 * k * (1 - k);
        const c = k * k;
        this.camera.position.set(
          a * from.position.x + b * via.x + c * to.position.x,
          a * from.position.y + b * via.y + c * to.position.y,
          a * from.position.z + b * via.z + c * to.position.z
        );
      } else {
        this.camera.position.lerpVectors(from.position, to.position, k);
      }
      this.yaw = this.lookYaw = from.yaw + (to.yaw - from.yaw) * k;
      this.pitch = this.lookPitch = from.pitch + (to.pitch - from.pitch) * k;
      if (t >= 1) {
        this.tween = null;
        this.anchorMouseLook();
        this.marker.visible = false;
        if (this.bubblePending) this.showBubble();
      }
      this.needsRender = true;
    } else {
      this.edgeTurn(elapsed);
      if (Math.abs(this.lookYaw - this.yaw) > 0.0004 || Math.abs(this.lookPitch - this.pitch) > 0.0004) {
        const rate = 1 - Math.exp(-elapsed / 65);
        this.yaw += (this.lookYaw - this.yaw) * rate;
        this.pitch += (this.lookPitch - this.pitch) * rate;
        this.needsRender = true;
      }
    }

    const dx = this.offsetTarget.x - this.offset.x;
    const dy = this.offsetTarget.y - this.offset.y;
    if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
      const rate = this.reducedMotion ? 1 : 1 - Math.exp(-elapsed / 130);
      this.offset.x += dx * rate;
      this.offset.y += dy * rate;
      this.needsRender = true;
    }

    if (!this.needsRender) return;
    this.needsRender = false;

    this.camera.rotation.set(this.pitch, this.yaw, 0);
    // Shift the picture so the painting stays centred in the space the interface leaves free.
    this.camera.setViewOffset(this.width, this.height, this.offset.x, this.offset.y, this.width, this.height);
    this.renderer.render(this.scene, this.camera);
    if (this.bubble && !this.bubble.hidden) this.placeBubble();
  }

  /* ---------- Movement ---------- */

  /**
   * @param {{ position: any, yaw: number, pitch: number }} view
   * @param {{ instant?: boolean, duration?: number, loose?: boolean }} [options]
   *   loose: a stroll with no fixed destination, which the visitor may break off by dragging to look around
   */
  glide(view, options = {}) {
    const from = { position: this.camera.position.clone(), yaw: this.yaw, pitch: this.pitch };
    const to = { position: view.position, yaw: from.yaw + shortestAngle(view.yaw - from.yaw), pitch: view.pitch };
    const distance = from.position.distanceTo(to.position);
    const duration =
      options.instant || this.reducedMotion ? 1 : options.duration || clamp(700 + distance * 240, 900, 2400);
    const via = this.detour(from.position, to.position);
    this.tween = { from, to, via, start: performance.now(), duration, loose: Boolean(options.loose) };
  }

  /**
   * The mascot stands in the middle of the room, so a straight walk from one wall to another would go
   * right through him. Returns the point to bend the walk around (the control point of a curve that
   * passes him at a comfortable distance), or null when the straight line is clear.
   * @param {any} from
   * @param {any} to
   */
  detour(from, to) {
    const body = this.mascot?.body;
    if (!body) return null;

    // Work on the floor plan, relative to his body.
    const ax = from.x - body.x;
    const az = from.z - body.z;
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = dx * dx + dz * dz;
    if (length < 0.01) return null;

    // The spot where the straight line comes closest to him.
    const along = -(ax * dx + az * dz) / length;
    if (along <= 0.02 || along >= 0.98) return null;
    const nearX = ax + dx * along;
    const nearZ = az + dz * along;
    const near = Math.hypot(nearX, nearZ);
    const clearance = body.radius + 0.7;
    if (near >= clearance) return null;

    // Pass on the side the line already leans towards; when it runs dead centre, either side will do.
    const span = Math.sqrt(length);
    const sideX = near > 0.05 ? nearX / near : -dz / span;
    const sideZ = near > 0.05 ? nearZ / near : dx / span;
    const passX = body.x + sideX * clearance;
    const passZ = body.z + sideZ * clearance;

    return new this.THREE.Vector3(
      2 * passX - (from.x + to.x) / 2,
      (from.y + to.y) / 2,
      2 * passZ - (from.z + to.z) / 2
    );
  }

  /**
   * Space the interface covers, in CSS pixels: the detail panel (x from the right on wide screens, y from
   * the bottom on phones) and, on phones, the buttons along the top.
   */
  cover() {
    const desktop = this.desktop.matches;
    const buttons = this.soundButton.getBoundingClientRect().bottom - this.el.getBoundingClientRect().top;
    const top = desktop ? 0 : Math.max(0, buttons + 8);
    if (this.panel.hidden) return { x: 0, y: 0, top };
    const rect = this.panel.getBoundingClientRect();
    return desktop ? { x: rect.width + 32, y: 0, top } : { x: 0, y: rect.height, top };
  }

  /** Shifts the picture so the work sits in the middle of the space the interface leaves free. */
  centreInFreeSpace() {
    const cover = this.cover();
    this.offsetTarget = { x: cover.x / 2, y: (cover.y - cover.top) / 2 };
  }

  /** Where to stand so the whole work fits in the free part of the screen. */
  viewFor(/** @type {any} */ stop) {
    const cover = this.cover();
    const freeX = (this.width - cover.x) / this.width;
    const freeY = (this.height - cover.y - cover.top) / this.height;
    const tan = Math.tan((this.camera.fov * Math.PI) / 360);

    const fitHeight = stop.h / 2 / (tan * freeY);
    const fitWidth = stop.w / 2 / (tan * this.camera.aspect * freeX);
    const run = Math.abs(stop.normal.x) > 0 ? ROOM.width : ROOM.depth;
    // Breathing room around the work; less on phones, where the sheet and buttons already frame it.
    const margin = this.desktop.matches ? 1.28 : 1.14;
    const distance = clamp(Math.max(fitHeight, fitWidth) * margin, 1.1, run - WALK_MARGIN);

    // Square-on at the height of the work's centre, like a camera on a tripod: tilting towards it
    // would make the canvas edges converge and a rectangular painting look like a trapezoid.
    const position = stop.center.clone().addScaledVector(stop.normal, distance);

    return { position, yaw: Math.atan2(stop.normal.x, stop.normal.z), pitch: 0 };
  }

  /**
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  focus(index, options = {}) {
    const stop = this.stops[index];
    if (!stop) return;
    this.current = index;
    this.hideBubble();

    if (stop.painting) {
      this.showPanel(stop.painting);
      this.setUrl(stop.painting.handle || null);
    } else {
      this.hidePanel();
      this.setUrl(null);
    }

    this.centreInFreeSpace();
    this.glide(this.viewFor(stop), options);
    this.updateCounter();
  }

  /** Re-frames the current work after the screen changes size. */
  refit() {
    this.centreInFreeSpace();
    this.glide(this.viewFor(this.stops[this.current]), { instant: true });
  }

  /** @param {number} direction */
  step(direction) {
    const count = this.stops.length;
    const next = this.current < 0 ? (direction > 0 ? 0 : count - 1) : (this.current + direction + count) % count;
    this.focus(next);
  }

  /** @param {any} point */
  walkTo(point) {
    const { THREE } = this;
    const limitX = ROOM.width / 2 - FLOOR_MARGIN;
    const limitZ = ROOM.depth / 2 - FLOOR_MARGIN;
    const target = new THREE.Vector3(clamp(point.x, -limitX, limitX), EYE_HEIGHT, clamp(point.z, -limitZ, limitZ));

    // Stop beside the mascot's ground, not on it: step out over whichever edge is nearest.
    if (this.mascot) {
      const halfX = this.mascot.halfX + 0.45;
      const halfZ = this.mascot.halfZ + 0.45;
      const insideX = halfX - Math.abs(target.x);
      const insideZ = halfZ - Math.abs(target.z);
      if (insideX > 0 && insideZ > 0) {
        if (insideX < insideZ) target.x = target.x >= 0 ? halfX : -halfX;
        else target.z = target.z >= 0 ? halfZ : -halfZ;
      }
    }

    const travel = target.clone().sub(this.camera.position);
    if (travel.length() < 0.25) return;

    this.leaveWork();
    this.marker.position.set(target.x, 0.008, target.z);
    this.marker.visible = true;
    this.glide({ position: target, yaw: Math.atan2(-travel.x, -travel.z), pitch: 0 }, { loose: true });
    this.updateCounter();
  }

  /** Steps away from the painting in view: no detail panel, nothing selected. */
  leaveWork() {
    this.hidePanel();
    this.hideBubble();
    this.current = -1;
    this.setUrl(null);
    this.offsetTarget = { x: 0, y: 0 };
  }

  /**
   * Stands in front of the mascot, off the ground he sits on and crouching when he is small, far enough
   * back to see him head to toe with room above his head for the speech bubble. He then says his piece.
   */
  visitMascot() {
    if (!this.mascot) return;
    const { halfZ, height } = this.mascot;
    const tan = Math.tan((this.camera.fov * Math.PI) / 360);
    const eye = clamp(height + 0.3, 0.9, EYE_HEIGHT);

    // Where his head and feet should land on screen, from -1 (bottom edge) to 1 (top edge).
    const headAt = 0.3;
    const feetAt = -0.85;
    const room = Math.atan(headAt * tan) - Math.atan(feetAt * tan);
    const limit = ROOM.depth / 2 - WALK_MARGIN;
    let distance = halfZ + 1;
    while (distance < limit && Math.atan2(height - eye, distance) + Math.atan2(eye, distance) > room) distance += 0.1;

    this.leaveWork();
    this.glide({
      position: new this.THREE.Vector3(0, eye, distance),
      yaw: 0,
      pitch: Math.atan2(height - eye, distance) - Math.atan(headAt * tan),
    });
    this.bubblePending = true;
    this.updateCounter();
  }

  /* ---------- Speech bubble ---------- */

  showBubble() {
    this.bubblePending = false;
    if (!this.bubble) return;
    this.bubble.hidden = false;
    this.needsRender = true;
  }

  hideBubble() {
    this.bubblePending = false;
    if (this.bubble) this.bubble.hidden = true;
  }

  /** Pins the bubble to the top of the mascot's head, wherever that is on screen right now. */
  placeBubble() {
    const { bubble, mascot } = this;
    if (!bubble || !mascot) return;

    const point = mascot.head.clone().project(this.camera);
    const inView = point.z < 1 && Math.abs(point.x) < 1.1 && Math.abs(point.y) < 1.1;
    bubble.style.visibility = inView ? '' : 'hidden';
    if (!inView) return;

    const x = ((point.x + 1) / 2) * this.width;
    const y = ((1 - point.y) / 2) * this.height;
    // Keep the whole bubble on screen and clear of the buttons; its tail still points at his head.
    const half = bubble.offsetWidth / 2;
    const centre = clamp(x, half + 12, Math.max(half + 12, this.width - half - 12));
    const bottom = Math.max(y - 16, bubble.offsetHeight + this.cover().top + 8);
    bubble.style.transform = `translate(${centre}px, ${bottom}px) translate(-50%, -100%)`;
    bubble.style.setProperty('--ag-tail', `${clamp(x - centre, 24 - half, half - 24)}px`);
  }

  /* ---------- Input ---------- */

  /** Desktop mouse-look is off while the visitor reads a painting, so the work stays framed beside the panel. */
  get mouseLookPaused() {
    return !this.entered || Boolean(this.tween) || !this.panel.hidden || !this.closeup.hidden;
  }

  /** The current view becomes "straight ahead" for where the mouse is now, so nothing jumps. */
  anchorMouseLook() {
    this.anchorYaw = this.lookYaw + this.mouse.nx * MOUSE_LOOK.yaw;
    this.anchorPitch = this.lookPitch + this.mouse.ny * MOUSE_LOOK.pitch;
  }

  /** @param {PointerEvent} event */
  mouseLook(event) {
    const rect = this.stage.getBoundingClientRect();
    const nx = clamp(((event.clientX - rect.left) / rect.width) * 2 - 1, -1, 1);
    const ny = clamp(((event.clientY - rect.top) / rect.height) * 2 - 1, -1, 1);
    if (!this.mouse.active) {
      this.mouse = { active: true, nx, ny };
      this.anchorMouseLook();
      return;
    }
    this.mouse.nx = nx;
    this.mouse.ny = ny;
    if (this.mouseLookPaused) {
      this.anchorMouseLook();
      return;
    }
    this.lookYaw = this.anchorYaw - nx * MOUSE_LOOK.yaw;
    this.lookPitch = clamp(this.anchorPitch - ny * MOUSE_LOOK.pitch, -0.7, 0.7);
  }

  /**
   * With the mouse held near the left or right edge the room keeps turning, so the visitor can look all the way round.
   * @param {number} elapsed
   */
  edgeTurn(elapsed) {
    if (!this.mouse.active || this.mouseLookPaused || this.stage.classList.contains('is-dragging')) return;
    const reach = Math.abs(this.mouse.nx) - MOUSE_LOOK.edge;
    if (reach <= 0) return;
    const speed = (reach / (1 - MOUSE_LOOK.edge)) * MOUSE_LOOK.turnSpeed;
    this.anchorYaw -= Math.sign(this.mouse.nx) * speed * (elapsed / 1000);
    this.lookYaw = this.anchorYaw - this.mouse.nx * MOUSE_LOOK.yaw;
  }

  bindEvents() {
    const stage = this.stage;
    /** @type {{ id: number, x: number, y: number, startX: number, startY: number, time: number, moved: boolean } | null} */
    let drag = null;

    stage.addEventListener('pointerdown', (event) => {
      if (!this.entered || drag) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY, time: performance.now(), moved: false };
      stage.setPointerCapture(event.pointerId);
    });

    stage.addEventListener('pointermove', (event) => {
      if (!this.entered) return;

      if (!drag) {
        if (event.pointerType === 'mouse') {
          this.hover(event);
          this.mouseLook(event);
        }
        return;
      }
      if (event.pointerId !== drag.id) return;

      if (!drag.moved && Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) > 6) {
        drag.moved = true;
        stage.classList.add('is-dragging');
        if (this.tween?.loose) {
          this.tween = null;
          this.marker.visible = false;
        }
      }

      if (drag.moved && !this.tween) {
        // Radians per pixel, so a full-width swipe turns the same amount on any screen.
        const sensitivity = ((this.camera.fov * Math.PI) / 180 / this.height) * 1.4;
        this.lookYaw += (event.clientX - drag.x) * sensitivity;
        this.lookPitch = clamp(this.lookPitch + (event.clientY - drag.y) * sensitivity, -0.7, 0.7);
      }
      drag.x = event.clientX;
      drag.y = event.clientY;
    });

    /** @param {PointerEvent} event */
    const release = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const tapped = !drag.moved && performance.now() - drag.time < 500 && event.type === 'pointerup';
      drag = null;
      stage.classList.remove('is-dragging');
      this.anchorMouseLook();
      if (tapped) this.tap(event);
    };
    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);
    stage.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.mouse.active = false;
    });

    this.enterButton.addEventListener('click', () => this.enter());
    this.el.querySelector('[data-exit]')?.addEventListener('click', () => this.exit());
    // Arrows live in the tour bar and, on phones, in the detail sheet that covers it.
    for (const button of this.el.querySelectorAll('[data-prev]')) button.addEventListener('click', () => this.step(-1));
    for (const button of this.el.querySelectorAll('[data-next]')) button.addEventListener('click', () => this.step(1));
    this.el.querySelector('[data-bubble-next]')?.addEventListener('click', () => this.focus(0));
    this.el.querySelector('[data-panel-close]')?.addEventListener('click', () => this.closePanel());
    this.el.querySelector('[data-closeup-close]')?.addEventListener('click', () => this.closeCloseup());
    this.soundButton.addEventListener('click', () => this.setSound(!this.soundOn, { remember: true }));

    window.addEventListener('keydown', (event) => {
      if (!this.entered) return;
      if (event.key === 'ArrowRight') this.step(1);
      else if (event.key === 'ArrowLeft') this.step(-1);
      else if (event.key === 'Escape') {
        if (!this.closeup.hidden) this.closeCloseup();
        else if (!this.panel.hidden) this.closePanel();
        else this.exit();
      } else return;
      event.preventDefault();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.soundOn) {
        this.resumeSound = true;
        this.setSound(false);
      } else if (!document.hidden && this.resumeSound) {
        this.resumeSound = false;
        if (this.entered) this.setSound(true);
      }
    });
  }

  /** @param {PointerEvent} event */
  pick(event) {
    const rect = this.stage.getBoundingClientRect();
    // Undo the view offset so picks land where the visitor sees things.
    const x = ((event.clientX - rect.left + this.offset.x) / rect.width) * 2 - 1;
    const y = -(((event.clientY - rect.top + this.offset.y) / rect.height) * 2 - 1);
    this.camera.clearViewOffset();
    this.raycaster.setFromCamera(new this.THREE.Vector2(x, y), this.camera);
    return this.raycaster.intersectObjects(this.pickables, false)[0];
  }

  /** @param {PointerEvent} event */
  tap(event) {
    const hit = this.pick(event);
    this.needsRender = true;
    if (!hit) return;

    const stop = hit.object.userData.stop;
    if (hit.object.userData.floor) {
      this.walkTo(hit.point);
    } else if (hit.object.userData.mascot) {
      this.visitMascot();
    } else if (this.canLookCloser(stop)) {
      this.openCloseup(this.stops[stop].painting);
    } else if (typeof stop === 'number') {
      this.focus(stop);
    }
  }

  /**
   * A second tap on the painting the visitor already stands in front of opens the close-up.
   * @param {unknown} index
   */
  canLookCloser(index) {
    return (
      typeof index === 'number' &&
      index === this.current &&
      !this.tween &&
      !this.panel.hidden &&
      Boolean(this.stops[index].painting?.imageLarge)
    );
  }

  /** @param {PointerEvent} event */
  hover(event) {
    if (this.tween) return;
    const hit = this.pick(event);
    const overWork = Boolean(hit && typeof hit.object.userData.stop === 'number');
    this.stage.classList.toggle('is-pointing', overWork || Boolean(hit?.object.userData.mascot));
    this.stage.classList.toggle('is-zoomable', overWork && this.canLookCloser(hit.object.userData.stop));

    const onFloor = Boolean(hit && hit.object.userData.floor);
    if (onFloor) this.marker.position.set(hit.point.x, 0.008, hit.point.z);
    this.marker.visible = onFloor;
    this.needsRender = true;
  }

  /* ---------- Interface ---------- */

  updateCounter() {
    const stop = this.stops[this.current];
    if (!stop) {
      this.counter.textContent = `${this.paintings.length} paintings`;
    } else if (stop.artist) {
      this.counter.textContent = 'About the artist';
    } else {
      this.counter.textContent = `${this.current + 1} / ${this.paintings.length} · ${stop.painting.title}`;
    }
    this.position.textContent = stop?.painting ? `${this.current + 1} / ${this.paintings.length}` : '';
  }

  /** Keeps the address shareable and lets the back button return to the same painting. */
  setUrl(/** @type {string | null} */ handle) {
    const url = new URL(location.href);
    if (handle) url.searchParams.set('painting', handle);
    else url.searchParams.delete('painting');
    history.replaceState(history.state, '', url);
  }

  /** @param {any} painting */
  showPanel(painting) {
    const body = this.panelBody;
    body.textContent = '';

    body.append(node('h2', 'art-gallery__panel-title', painting.title));

    const meta = node('p', 'art-gallery__panel-meta');
    if (painting.available && painting.price) meta.append(node('span', 'art-gallery__panel-price', painting.price));
    if (!painting.placeholder) {
      meta.append(
        painting.available
          ? node('span', 'art-gallery__status', 'Available')
          : node('span', 'art-gallery__status art-gallery__status--sold', 'Sold')
      );
    }
    if (painting.sizeKnown) meta.append(node('span', 'art-gallery__panel-size', sizeText(painting)));
    body.append(meta);

    // Buttons come straight after the facts, so "View and buy" is on screen without scrolling the phone sheet.
    const actions = node('div', 'art-gallery__actions');
    if (painting.url) {
      if (painting.available) {
        actions.append(link('art-gallery__button', 'View and buy', painting.url));
      } else {
        actions.append(link('art-gallery__button', 'Enquire about this work', this.config.enquiryUrl || '/'));
        actions.append(link('art-gallery__button art-gallery__button--ghost', 'Painting page', painting.url));
      }
    }
    if (painting.imageLarge) {
      const look = node('button', 'art-gallery__button art-gallery__button--ghost', 'Look closer');
      look.setAttribute('type', 'button');
      look.setAttribute('data-look-closer', '');
      look.addEventListener('click', () => this.openCloseup(painting));
      actions.append(look);
    }
    if (actions.childElementCount) body.append(actions);

    if (painting.placeholder) {
      body.append(node('p', 'art-gallery__panel-text', 'Placeholder canvas. Choose a paintings collection in the theme editor to hang the real work here.'));
    } else if (painting.story?.length || painting.details?.length) {
      const [lead, ...rest] = painting.story || [];
      if (lead) body.append(node('p', 'art-gallery__panel-lead', lead));
      for (const paragraph of rest) body.append(node('p', 'art-gallery__panel-text', paragraph));

      if (painting.details?.length) {
        const list = node('dl', 'art-gallery__details');
        for (const detail of painting.details) {
          // The size already sits in the line under the title.
          if (painting.sizeKnown && /^(size|dimensions?)$/i.test(detail.label)) continue;
          const row = node('div', 'art-gallery__detail');
          if (detail.label) row.append(node('dt', '', detail.label));
          row.append(node('dd', '', detail.value));
          list.append(row);
        }
        body.append(list);
      }
    } else if (painting.description) {
      body.append(node('p', 'art-gallery__panel-text', painting.description));
    }

    if (painting.sizeKnown) body.append(scaleFigure(painting));

    if (painting.garments?.length) {
      const wear = node('div', 'art-gallery__wear');
      wear.append(node('p', 'art-gallery__wear-label', 'Wear this painting'));
      for (const garment of painting.garments) {
        const anchor = link('', '', garment.url);
        if (garment.image) {
          const image = document.createElement('img');
          image.src = garment.image;
          image.alt = '';
          image.loading = 'lazy';
          anchor.append(image);
        }
        anchor.append(node('span', '', garment.title));
        wear.append(anchor);
      }
      body.append(wear);
    }

    this.panel.hidden = false;
    this.panel.scrollTop = 0;
    this.el.classList.add('has-panel');
  }

  hidePanel() {
    this.panel.hidden = true;
    this.el.classList.remove('has-panel');
  }

  /** Closing the panel keeps the visitor where they stand and gives the whole screen back to the room. */
  closePanel() {
    this.hidePanel();
    this.centreInFreeSpace();
    this.needsRender = true;
  }

  /* ---------- Close-up viewer ---------- */

  /** @param {any} painting */
  openCloseup(painting) {
    const stage = this.closeupStage;
    stage.textContent = '';
    this.closeup.hidden = false;

    const image = document.createElement('img');
    image.alt = painting.title;
    image.draggable = false;
    stage.append(image);
    /** @type {HTMLElement | null} */ (this.closeup.querySelector('[data-closeup-close]'))?.focus();

    // The picture is laid out to fill the stage and zoomed from there (scale 1 = whole painting in view).
    const aspect = painting.aspect || painting.w / painting.h;
    const view = { scale: 1, x: 0, y: 0, max: 4, width: 0, height: 0 };

    const layout = () => {
      view.width = Math.min(stage.clientWidth, stage.clientHeight * aspect);
      view.height = view.width / aspect;
      image.style.width = `${view.width}px`;
      image.style.height = `${view.height}px`;
    };

    /** Keeps the picture on screen: centred while it is smaller than the stage, edge to edge once it is larger. */
    const apply = () => {
      const width = view.width * view.scale;
      const height = view.height * view.scale;
      const spareX = stage.clientWidth - width;
      const spareY = stage.clientHeight - height;
      view.x = spareX >= 0 ? spareX / 2 : clamp(view.x, spareX, 0);
      view.y = spareY >= 0 ? spareY / 2 : clamp(view.y, spareY, 0);
      image.style.transform = `translate(${view.x}px, ${view.y}px) scale(${view.scale})`;
    };

    /** Zooms around a point of the stage. @param {number} factor @param {number} cx @param {number} cy */
    const zoom = (factor, cx, cy) => {
      const scale = clamp(view.scale * factor, 1, view.max);
      const ratio = scale / view.scale;
      view.x = cx - (cx - view.x) * ratio;
      view.y = cy - (cy - view.y) * ratio;
      view.scale = scale;
      apply();
    };

    layout();
    apply();

    // Open at once with the file the room already loaded, then swap in the full-detail one when it is ready.
    image.src = painting.image || painting.imageLarge;
    this.closeupHint.textContent = 'Loading full detail…';
    const sharp = new Image();
    sharp.src = painting.imageLarge;
    sharp
      .decode()
      .then(() => {
        if (!image.isConnected) return;
        image.src = sharp.src;
        // Zoom in until one pixel of the photograph covers one and a half on screen.
        view.max = Math.max(4, (1.5 * sharp.naturalWidth) / view.width);
      })
      .catch(() => {
        /* too large for this device: the room's file stays */
      })
      .finally(() => {
        if (image.isConnected) this.closeupHint.textContent = this.closeupHintText;
      });

    /** @type {Map<number, { x: number, y: number }>} */
    const pointers = new Map();
    const controller = new AbortController();
    const { signal } = controller;
    this.closeupAbort = controller;

    const resized = new ResizeObserver(() => {
      layout();
      apply();
    });
    resized.observe(stage);
    signal.addEventListener('abort', () => resized.disconnect());

    stage.addEventListener(
      'pointerdown',
      (event) => {
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        stage.setPointerCapture(event.pointerId);
      },
      { signal }
    );

    stage.addEventListener(
      'pointermove',
      (event) => {
        const previous = pointers.get(event.pointerId);
        if (!previous) return;
        const rect = stage.getBoundingClientRect();

        if (pointers.size === 2) {
          const other = [...pointers.entries()].find(([id]) => id !== event.pointerId)?.[1];
          if (other) {
            const before = Math.hypot(previous.x - other.x, previous.y - other.y);
            const after = Math.hypot(event.clientX - other.x, event.clientY - other.y);
            const cx = (event.clientX + other.x) / 2 - rect.left;
            const cy = (event.clientY + other.y) / 2 - rect.top;
            if (before > 0) zoom(after / before, cx, cy);
          }
        } else {
          view.x += event.clientX - previous.x;
          view.y += event.clientY - previous.y;
          apply();
        }
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      },
      { signal }
    );

    /** @param {PointerEvent} event */
    const lift = (event) => pointers.delete(event.pointerId);
    stage.addEventListener('pointerup', lift, { signal });
    stage.addEventListener('pointercancel', lift, { signal });

    stage.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        const rect = stage.getBoundingClientRect();
        zoom(Math.exp(-event.deltaY * 0.0015), event.clientX - rect.left, event.clientY - rect.top);
      },
      { signal, passive: false }
    );

    stage.addEventListener(
      'dblclick',
      (event) => {
        const rect = stage.getBoundingClientRect();
        zoom(view.scale > 1.5 ? 1 / view.scale : 2.5, event.clientX - rect.left, event.clientY - rect.top);
      },
      { signal }
    );
  }

  closeCloseup() {
    this.closeupAbort?.abort();
    this.closeup.hidden = true;
    this.closeupStage.textContent = '';
    this.closeupHint.textContent = this.closeupHintText;
    /** @type {HTMLElement | null} */ (this.panelBody.querySelector('[data-look-closer]'))?.focus();
  }

  /* ---------- Sound ---------- */

  /** True when the visitor switched the music off earlier in this session. */
  muted() {
    try {
      return sessionStorage.getItem(MUTED_KEY) === '1';
    } catch (_) {
      return false;
    }
  }

  /**
   * @param {boolean} on
   * @param {{ remember?: boolean }} [options] remember: the visitor chose this, so keep it for the session
   */
  async setSound(on, options = {}) {
    if (options.remember) {
      try {
        if (on) sessionStorage.removeItem(MUTED_KEY);
        else sessionStorage.setItem(MUTED_KEY, '1');
      } catch (_) {
        /* storage blocked */
      }
    }

    if (on && !this.sound) this.sound = this.config.audioUrl ? fileSound(this.config.audioUrl) : jazzTrio();
    if (!this.sound) return;

    this.soundOn = on;
    this.updateSoundButton();
    if (!on) {
      this.sound.pause();
      return;
    }

    const started = await this.sound.play();
    if (started || !this.soundOn) return;

    // The browser refused to play without a touch (e.g. returning via the back button): start on the next one.
    this.soundOn = false;
    this.updateSoundButton();
    /** @param {Event} event */
    const retry = (event) => {
      if (event.target instanceof Element && event.target.closest('[data-sound]')) return;
      this.el.removeEventListener('pointerdown', retry);
      if (this.entered && !this.soundOn && !this.muted()) this.setSound(true);
    };
    this.el.addEventListener('pointerdown', retry);
  }

  updateSoundButton() {
    this.soundButton.textContent = this.soundOn ? 'Sound on' : 'Sound off';
    this.soundButton.setAttribute('aria-pressed', String(this.soundOn));
  }
}

/* ---------- Helpers ---------- */

/**
 * Repeatable random numbers from 0 to 1, so generated textures look the same on every visit.
 * @param {number} seed
 */
function seededRandom(seed) {
  return () => ((seed = (seed * 16807) % 2147483647) % 1000) / 1000;
}

/**
 * A copy of the picture cropped to its visible part, or null when nothing around it is transparent.
 * The crop sits on `fill`, so any soft cut-out edge blends into the canvas edge colour instead of black.
 * @param {any} image
 * @param {string} fill
 * @returns {HTMLCanvasElement | null}
 */
function trimTransparent(image, fill) {
  if (!image?.width || !image?.height) return null;

  // Find the visible area on a small copy; reading every pixel of a 1600 px photo is not needed for a margin.
  const probeScale = Math.min(1, 256 / Math.max(image.width, image.height));
  const probe = document.createElement('canvas');
  probe.width = Math.max(1, Math.round(image.width * probeScale));
  probe.height = Math.max(1, Math.round(image.height * probeScale));
  const probeContext = /** @type {CanvasRenderingContext2D} */ (probe.getContext('2d', { willReadFrequently: true }));
  probeContext.drawImage(image, 0, 0, probe.width, probe.height);

  let pixels;
  try {
    pixels = probeContext.getImageData(0, 0, probe.width, probe.height).data;
  } catch {
    return null;
  }

  let left = probe.width;
  let right = -1;
  let top = probe.height;
  let bottom = -1;
  let transparent = false;
  for (let y = 0; y < probe.height; y++) {
    for (let x = 0; x < probe.width; x++) {
      if (pixels[(y * probe.width + x) * 4 + 3] < 128) {
        transparent = true;
        continue;
      }
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  if (!transparent || right < left || bottom < top) return null;

  // Back to full size, one probe pixel inwards so no see-through fringe survives.
  const unit = 1 / probeScale;
  const sx = Math.min(image.width - 1, Math.ceil((left + 1) * unit));
  const sy = Math.min(image.height - 1, Math.ceil((top + 1) * unit));
  const sw = Math.max(1, Math.floor(right * unit) - sx);
  const sh = Math.max(1, Math.floor(bottom * unit) - sy);

  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const context = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
  context.fillStyle = fill;
  context.fillRect(0, 0, sw, sh);
  context.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);
  return canvas;
}

/**
 * Frees a texture and the decoded picture behind it.
 * @param {any} texture
 */
function discardTexture(texture) {
  if (!texture) return;
  texture.image?.close?.();
  texture.dispose();
}

/**
 * Darkens the rim of a surface where it meets its neighbours (the soft shade found in every corner of a room).
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width
 * @param {number} height
 * @param {number} reachX how far the shade reaches in from the left and right edges; 0 leaves them alone
 * @param {number} reachY the same for the top and bottom edges
 * @param {number} strength opacity at the very edge
 */
function shadeEdges(ctx, width, height, reachX, reachY, strength) {
  /** @type {[number, number, number, number][]} */
  const sides = [];
  if (reachX) sides.push([0, 0, reachX, 0], [width, 0, width - reachX, 0]);
  if (reachY) sides.push([0, 0, 0, reachY], [0, height, 0, height - reachY]);

  for (const [x0, y0, x1, y1] of sides) {
    const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
    gradient.addColorStop(0, `rgba(44, 40, 33, ${strength})`);
    gradient.addColorStop(0.4, `rgba(44, 40, 33, ${strength * 0.3})`);
    gradient.addColorStop(1, 'rgba(44, 40, 33, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }
}

/**
 * @param {string} tag
 * @param {string} className
 * @param {string} [text]
 */
function node(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
}

/**
 * @param {string} className
 * @param {string} text
 * @param {string} href
 */
function link(className, text, href) {
  const anchor = /** @type {HTMLAnchorElement} */ (node('a', className, text));
  anchor.href = href;
  return anchor;
}

/**
 * A product description split into its story lines and its "Label: value" facts (the list items).
 * Parsed by the browser as an inert document and read as text only, so no markup from it reaches the page.
 * @param {string | undefined} html
 * @param {string | undefined} title
 * @returns {{ story: string[], details: { label: string, value: string }[] }}
 */
function parseDescription(html, title) {
  if (!html) return { story: [], details: [] };
  const doc = new DOMParser().parseFromString(html, 'text/html');
  /** @param {string | null} text */
  const tidy = (text) => (text || '').replace(/\s+/g, ' ').trim();

  const details = [...doc.querySelectorAll('li')]
    .map((item) => {
      const text = tidy(item.textContent);
      const colon = text.indexOf(':');
      return colon > 0 && colon < 30
        ? { label: text.slice(0, colon).trim(), value: text.slice(colon + 1).trim() }
        : { label: '', value: text };
    })
    .filter((detail) => detail.value);
  doc.querySelectorAll('ul, ol').forEach((list) => list.remove());

  // Line breaks and block ends become separate lines.
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6').forEach((block) => block.append('\n'));
  const heading = tidy(title).toLowerCase();
  const story = (doc.body.textContent || '')
    .split('\n')
    .map(tidy)
    .filter((line) => line && line.toLowerCase() !== heading && !/^(artwork )?(information|details)$/i.test(line));

  return { story, details };
}

/** @param {{ w: number, h: number, sizeLabel?: string }} painting */
function sizeText(painting) {
  if (painting.sizeLabel) return painting.sizeLabel;
  return `${Math.round(painting.w * 100)} × ${Math.round(painting.h * 100)} cm`;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} maxWidth
 */
function wrapText(ctx, text, maxWidth) {
  /** @type {string[]} */
  const lines = [];
  let line = '';
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * The painting drawn next to a 175 cm person, hung at gallery height.
 * @param {{ w: number, h: number }} painting
 */
function scaleFigure(painting) {
  const person = 175;
  const width = painting.w * 100;
  const height = painting.h * 100;
  const top = Math.max(person, HANG_HEIGHT * 100 + height / 2) + 10;
  const total = 60 + 30 + width;
  const ns = 'http://www.w3.org/2000/svg';

  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', `0 0 ${total} ${top}`);
  svg.setAttribute('aria-hidden', 'true');

  /** @param {string} tag @param {Record<string, string | number>} attributes */
  const shape = (tag, attributes) => {
    const element = document.createElementNS(ns, tag);
    for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, String(value));
    svg.append(element);
  };

  shape('circle', { cx: 30, cy: top - person + 12, r: 12, fill: '#bdbdbd' });
  shape('rect', { x: 10, y: top - person + 28, width: 40, height: person - 28, rx: 16, fill: '#bdbdbd' });
  shape('rect', {
    x: 90,
    y: top - HANG_HEIGHT * 100 - height / 2,
    width,
    height,
    fill: '#111',
  });
  shape('line', { x1: 0, y1: top - 0.5, x2: total, y2: top - 0.5, stroke: '#bdbdbd', 'stroke-width': 1 });

  const figure = node('div', 'art-gallery__scale');
  figure.append(svg, node('span', '', `True to scale, next to a person of ${person} cm`));
  return figure;
}

/**
 * A sound source the gallery can switch on and off. play() resolves to false when the browser blocks audio.
 * @typedef {{ play: () => Promise<boolean>, pause: () => void }} GallerySound
 */

/**
 * @param {string} url
 * @returns {GallerySound}
 */
function fileSound(url) {
  const audio = new Audio(url);
  audio.loop = true;
  audio.volume = 0.35;
  return {
    play: () =>
      audio.play().then(
        () => true,
        () => false
      ),
    pause: () => audio.pause(),
  };
}

/**
 * Soft generated jazz trio (electric piano, walking bass, brushed ride), used until a recorded track is set.
 * Two four-bar turnarounds in C alternate; comping and the sparse lead vary each bar so it never loops exactly.
 * @returns {GallerySound | null}
 */
function jazzTrio() {
  const AudioContextClass = window.AudioContext || /** @type {any} */ (window).webkitAudioContext;
  if (!AudioContextClass) return null;

  const context = new AudioContextClass();
  const master = context.createGain();
  master.gain.value = 0;
  const warmth = context.createBiquadFilter();
  warmth.type = 'lowpass';
  warmth.frequency.value = 3400;
  master.connect(warmth).connect(context.destination);

  const bassTone = context.createBiquadFilter();
  bassTone.type = 'lowpass';
  bassTone.frequency.value = 380;
  bassTone.connect(master);

  const cymbalTone = context.createBiquadFilter();
  cymbalTone.type = 'highpass';
  cymbalTone.frequency.value = 7000;
  cymbalTone.connect(master);

  const noise = context.createBuffer(1, context.sampleRate * 0.3, context.sampleRate);
  const samples = noise.getChannelData(0);
  for (let i = 0; i < samples.length; i++) samples[i] = Math.random() * 2 - 1;

  const beat = 60 / 84;
  const swing = beat * 0.64;
  const hz = (/** @type {number} */ midi) => 440 * 2 ** ((midi - 69) / 12);

  // Rootless piano voicings and a walking bass line per bar (MIDI notes); each bass line leads into the next root.
  const sections = [
    [
      { chord: [53, 57, 60, 64], bass: [38, 41, 45, 44] }, // Dm9
      { chord: [53, 57, 59, 64], bass: [43, 47, 50, 49] }, // G13
      { chord: [52, 55, 59, 62], bass: [48, 52, 55, 46] }, // Cmaj9
      { chord: [55, 59, 61, 65], bass: [45, 43, 41, 39] }, // A7b13
    ],
    [
      { chord: [57, 60, 64, 67], bass: [41, 45, 48, 47] }, // Fmaj9
      { chord: [56, 60, 62, 67], bass: [46, 50, 53, 41] }, // Bb13
      { chord: [55, 59, 62, 66], bass: [40, 43, 47, 46] }, // Em9
      { chord: [55, 58, 61, 64], bass: [45, 49, 52, 39] }, // A7b9
    ],
  ];

  /**
   * @param {OscillatorType} type
   * @param {number} frequency
   * @param {number} time
   * @param {number} length
   * @param {number} level
   * @param {AudioNode} [destination]
   */
  const tone = (type, frequency, time, length, level, destination = master) => {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(level, time + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + length);
    oscillator.connect(gain).connect(destination);
    oscillator.start(time);
    oscillator.stop(time + length + 0.05);
  };

  /** @param {number} time @param {number} level */
  const brush = (time, level) => {
    const source = context.createBufferSource();
    const gain = context.createGain();
    source.buffer = noise;
    gain.gain.setValueAtTime(level, time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.14);
    source.connect(gain).connect(cymbalTone);
    source.start(time);
    source.stop(time + 0.2);
  };

  /** @param {number[]} chord @param {number} time @param {number} length @param {number} level */
  const comp = (chord, time, length, level) => {
    for (const note of chord) {
      tone('sine', hz(note), time, length, level);
      tone('triangle', hz(note), time, length * 0.5, level * 0.25);
    }
  };

  /** @param {number} index @param {number} time */
  const scheduleBar = (index, time) => {
    const bar = sections[Math.floor(index / 4) % sections.length][index % 4];

    comp(bar.chord, time, beat * 3.2, 0.05);
    if (Math.random() < 0.65) comp(bar.chord, time + beat + swing, beat * 1.4, 0.035);

    bar.bass.forEach((note, i) => tone('triangle', hz(note), time + i * beat, beat * 0.95, 0.3, bassTone));

    // Ride pattern: ding, ding-a, ding, ding-a.
    for (let i = 0; i < 4; i++) {
      brush(time + i * beat, 0.05);
      if (i % 2 === 1) brush(time + i * beat + swing, 0.03);
    }

    if (Math.random() < 0.4) {
      for (const offset of [beat + swing, beat * 2, beat * 3 + swing]) {
        if (Math.random() < 0.3) continue;
        const note = bar.chord[Math.floor(Math.random() * bar.chord.length)] + 12;
        tone('sine', hz(note), time + offset, beat * 1.2, 0.06);
      }
    }
  };

  let timer = 0;
  let suspendTimer = 0;
  let nextBar = 0;
  let barIndex = 0;

  const tick = () => {
    while (nextBar < context.currentTime + 1.2) {
      scheduleBar(barIndex++, nextBar);
      nextBar += beat * 4;
    }
  };

  /** @param {number} value */
  const fade = (value) => {
    const now = context.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(value, now + 0.8);
  };

  return {
    play: async () => {
      window.clearTimeout(suspendTimer);
      // resume() stays pending until the browser allows audio, so do not wait on it forever.
      await Promise.race([context.resume().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 400))]);
      if (context.state !== 'running') return false;

      if (!timer) {
        nextBar = Math.max(nextBar, context.currentTime + 0.1);
        tick();
        timer = window.setInterval(tick, 300);
      }
      fade(0.5);
      return true;
    },
    pause: () => {
      fade(0);
      window.clearInterval(timer);
      timer = 0;
      suspendTimer = window.setTimeout(() => context.suspend(), 1000);
    },
  };
}
